import { useEffect, useRef, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { useArrowKeyNav, useClickOutside } from '@nv/hooks'
import ElipsisIcon from '@nv/images/design/elipsis-icon'
import { addPassiveListener } from '@nv/utils'
import { v4 as uuidv4 } from 'uuid'
import styles from './ActionDropdown.module.scss'

const isScrollable = (node) => {
  if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
    return false
  }
  const style = getComputedStyle(node)
  return ['overflow', 'overflow-x', 'overflow-y'].some((propertyName) => {
    const value = style.getPropertyValue(propertyName)
    return value === 'auto' || value === 'scroll'
  })
}

export const getScrollAllParents = (node) => {
  let currentParent = node.parentElement
  const parents = []
  while (currentParent) {
    if (isScrollable(currentParent)) parents.push(currentParent)
    currentParent = currentParent.parentElement
  }
  parents.push(document.scrollingElement || document.documentElement)
  return parents
}

export const ActionDropdown = ({ values = [], onCallback, ariaLabel }: any) => {
  const [open, setOpen] = useState(false)
  const actionDropdownRef = useRef(null)
  const [flipped, setFlipped] = useState(false)
  const openDropdown = () => setOpen(true)
  const closeDropdown = () => setOpen(false)
  const dropdownId = uuidv4()

  const positionCheck = () => {
    if (!buttonRef.current || !actionDropdownRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const dropdown = actionDropdownRef.current
    const dropdownHeight = dropdown.offsetHeight || 150
    const spaceBelow = window.innerHeight - buttonRect.bottom
    const spaceAbove = buttonRect.top
    const shouldOpenUpwards = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight

    setFlipped(shouldOpenUpwards)

    dropdown.style.top = shouldOpenUpwards ? `${buttonRect.top - dropdownHeight + 40}px` : `${buttonRect.bottom - 40}px`
    dropdown.style.right = `${window.innerWidth - buttonRect.left + 8}px`
  }

  const { handleContainerKeyDown, handleOptionsKeyDown, optionRefs, buttonRef, containerRef } = useArrowKeyNav({
    options: values,
    onCallback: (item) => typeof onCallback === 'function' && onCallback(item.value),
    onFocus: openDropdown,
    onBlur: closeDropdown,
    activation: 'enter',
  })

  useClickOutside(containerRef, closeDropdown)

  // Check on scroll
  useEffect(() => {
    if (open) positionCheck()
  }, [open])

  // Check on scroll
  useEffect(() => {
    if (open) {
      const parents = getScrollAllParents(buttonRef.current)
      const listoffunctions = parents.map((parent) => addPassiveListener('scroll', positionCheck, parent))

      return () =>
        listoffunctions.forEach((removeListener) => {
          removeListener()
        })
    }
  }, [open])

  // Check on resize
  useEffect(() => {
    if (open) return addPassiveListener('resize', positionCheck)
  }, [open])

  return (
    <div className={styles['dropdown']} ref={containerRef} aria-label={ariaLabel}>
      <NVButton
        id={`actionDropdownButton-${dropdownId}`}
        variant='iconOnly'
        icon={<ElipsisIcon />}
        classes={{ container: styles['elipsis-icon'] }}
        ref={buttonRef}
        tabIndex={0}
        className={styles['elipsis-icon']}
        onClick={openDropdown}
        onKeyDown={handleContainerKeyDown}
        role='button'
        ariaLabel={ariaLabel}
        ariaHaspopup='menu'
        ariaExpanded={open}
      />

      {open && (
        <div
          className={`${styles['options']} ${flipped ? styles['flipped'] : ''}`}
          ref={actionDropdownRef}
          role='menu'
          aria-labelledby={`actionDropdownButton-${dropdownId}`}
        >
          {values.map((value, index) => (
            <NVButton
              key={index}
              width='100%'
              variant='text'
              stretchContent={true}
              text={value.label}
              icon={value.icon && value.icon}
              classes={{ container: styles['option'] }}
              ref={(el) => (optionRefs.current[index] = el!)}
              onClick={() => {
                value.callback ? value.callback() : onCallback(value.value)
                closeDropdown()
              }}
              onKeyDown={handleOptionsKeyDown}
              role='menuitem'
              tabIndex={-1}
            />
          ))}
        </div>
      )}
    </div>
  )
}
